import React, { useState } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Link,
  Stack,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import { useTranslation } from "react-i18next";
import { useBrand } from "../brand/BrandContext";
import { resolveBrandContacts } from "../brand/contacts";

const faqItems = [
  { categoryKey: "faqPage.categoryBaggage", questionKey: "faqPage.q1", answerKey: "faqPage.a1" },
  { categoryKey: "faqPage.categoryBaggage", questionKey: "faqPage.q2", answerKey: "faqPage.a2" },
  { categoryKey: "faqPage.categoryBaggage", questionKey: "faqPage.q3", answerKey: "faqPage.a3" },
  { categoryKey: "faqPage.categoryPayment", questionKey: "faqPage.q4", answerKey: "faqPage.a4" },
  { categoryKey: "faqPage.categoryTicketsRefund", questionKey: "faqPage.q5", answerKey: "faqPage.a5" },
  { categoryKey: "faqPage.categoryTicketsRefund", questionKey: "faqPage.q6", answerKey: "faqPage.a6" },
  { categoryKey: "faqPage.categoryTicketsRefund", questionKey: "faqPage.q7", answerKey: "faqPage.a7" },
  { categoryKey: "faqPage.categoryTravel", questionKey: "faqPage.q8", answerKey: "faqPage.a8" },
  { categoryKey: "faqPage.categoryTravel", questionKey: "faqPage.q9", answerKey: "faqPage.a9" },
  { categoryKey: "faqPage.categoryTravel", questionKey: "faqPage.q10", answerKey: "faqPage.a10" },
];

const FAQPage = () => {
  const { t } = useTranslation("common");
  const brand = useBrand();
  const COMPANY_CONTACTS = resolveBrandContacts(brand);
  const allCatKey = "faqPage.allQuestions";
  const [selectedCategoryKey, setSelectedCategoryKey] = useState(allCatKey);

  const allCategories = [
    allCatKey,
    ...Array.from(new Set(faqItems.map((item) => item.categoryKey))),
  ];

  const filteredFaq =
    selectedCategoryKey === allCatKey
      ? faqItems
      : faqItems.filter((item) => item.categoryKey === selectedCategoryKey);

  return (
    <Box sx={{ py: 6, px: { xs: 2, sm: 4 }, minHeight: "40vh" }}>
      <Container maxWidth="lg">
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, mb: 2, color: "text.primary", textAlign: "left" }}
        >
          {t("faqPage.title")}
        </Typography>
        <Typography
          variant="body1"
          sx={{ color: "text.secondary", mb: 4, maxWidth: 700, textAlign: "left" }}
        >
          {t("faqPage.subtitle")}
        </Typography>

        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {allCategories.map((catKey) => {
                const selected = selectedCategoryKey === catKey;
                return (
                  <Chip
                    key={catKey}
                    label={t(catKey)}
                    clickable
                    onClick={() => setSelectedCategoryKey(catKey)}
                    color={selected ? "primary" : "default"}
                    variant={selected ? "filled" : "outlined"}
                    sx={{
                      fontWeight: 500,
                      fontSize: "14px",
                      borderRadius: 1,
                      px: 1,
                    }}
                  />
                );
              })}
            </Box>
          </Grid>

          <Grid item xs={12} md={8}>
            {filteredFaq.map((item, idx) => (
              <Accordion
                key={idx}
                elevation={0}
                sx={{
                  mb: 2,
                  bgcolor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "8px !important",
                  boxShadow: "none",
                  "&:before": { display: "none" },
                  "&.Mui-expanded": {
                    boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.08)",
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: "primary.main" }} />}
                >
                  <Typography fontWeight={600} color="text.primary">
                    {t(item.questionKey)}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography color="text.secondary">{t(item.answerKey)}</Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Grid>
        </Grid>

        <Box
          sx={{
            mt: 6,
            py: 4,
            px: 3,
            bgcolor: "primary.main",
            borderRadius: 2,
            color: "primary.contrastText",
            textAlign: "center",
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
            {t("faqPage.noAnswerTitle")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, opacity: 0.92 }}>
            {t("faqPage.noAnswerText")}
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="center"
            alignItems="center"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <PhoneIcon fontSize="small" />
              <Link
                href={`tel:${COMPANY_CONTACTS.primaryPhoneTel}`}
                color="inherit"
                underline="hover"
                fontWeight={600}
              >
                {COMPANY_CONTACTS.primaryPhone}
              </Link>
            </Stack>
            <Typography sx={{ display: { xs: "none", sm: "block" }, opacity: 0.6 }}>
              |
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <EmailIcon fontSize="small" />
              <Link
                href={`mailto:${COMPANY_CONTACTS.email}`}
                color="inherit"
                underline="hover"
                fontWeight={600}
              >
                {COMPANY_CONTACTS.email}
              </Link>
            </Stack>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default FAQPage;
